import React from 'react';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import SlideshowRounded from '@mui/icons-material/SlideshowRounded';
import TimelineRounded from '@mui/icons-material/TimelineRounded';
import FilterAltRounded from '@mui/icons-material/FilterAltRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import TravelExploreRounded from '@mui/icons-material/TravelExploreRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import CampaignRounded from '@mui/icons-material/CampaignRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import MapRounded from '@mui/icons-material/MapRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';

// Single source of truth for the Solutions area. Each dedicated audience page
// (/solutions/<slug>) renders one of these case lists as a focused page in the
// shared design language (centered hero, proof panel, icon-chip rows). House
// style: no em dashes.

export const PE_CASES = [
  { Icon: AssessmentRounded, title: 'The quarterly board pack', body: 'Every portco, every quarter. Point Restack at last quarter’s deck and the new numbers, and the board pack rebuilds itself on your template, KPIs and the value-creation bridge included.' },
  { Icon: FactCheckRounded, title: 'The IC memo and committee deck', body: 'The IC memo and committee deck take shape from model outputs, diligence, and call notes, with figures traceable in one click.' },
  { Icon: TimelineRounded, title: 'The value-creation and 100-day plan', body: 'The value-creation bridge, initiatives, and KPIs, rendered the way PE shows them and kept current through the hold.' },
  { Icon: FilterAltRounded, title: 'Pipeline and screening pages', body: 'The weekly pipeline deck and one-page target summaries, refreshed from the tracker the same way every week.' },
  { Icon: SlideshowRounded, title: 'Due diligence decks', body: 'Commercial, financial, and operational findings synthesized onto the mandate template, defensible to the IC.' },
  { Icon: GroupsRounded, title: 'Management and exit materials', body: 'Management decks and exit materials built alongside your bankers, on the mandate template.' },
  { Icon: AccountBalanceRounded, title: 'Fund and LP reporting', body: 'LP decks and annual-meeting materials, on the same template every cycle.' },
  { Icon: AutorenewRounded, title: 'Recurring data refresh', body: 'Attach the new Excel or CSV; the charts and tables update in place, on your template, the same way every cycle.' },
];

export const GE_CASES = [
  { Icon: MapRounded, title: 'Market maps and sector landscapes', body: 'Filings, reports, and databases become a positioned landscape on your firm template, including the market Mekko rendered natively in your slide and editable like any chart.' },
  { Icon: BarChartRounded, title: 'Bottom-up market sizing', body: 'TAM, SAM, and SOM built bottom-up and cross-checked top-down, with the methodology cited so it holds up.' },
  { Icon: FactCheckRounded, title: 'Investment thesis decks', body: 'The why-now and where-it-goes turned into a partner-ready thesis, every figure cited.' },
  { Icon: FilterAltRounded, title: 'Sourcing and screening memos', body: 'A new company screened against the thesis: fit, traction, and red flags on a one-pager.' },
  { Icon: GroupsRounded, title: 'The quarterly board pack', body: 'KPI and cohort decks rebuilt from the latest numbers, on the same board template each quarter.' },
  { Icon: SwapHorizRounded, title: 'Competitive landscape', body: 'Comparable companies, funding, and momentum laid out and refreshed as the market moves.' },
  { Icon: AutorenewRounded, title: 'Recurring portfolio reporting', body: 'Attach the new metrics; the charts and tables update in place for the quarterly pack.' },
];

export const CONSULTING_CASES = [
  { Icon: SlideshowRounded, title: 'SteerCo decks and final reports', body: 'Research becomes the client deliverable on your firm master: action titles, the Pyramid Principle, and the so-what, structured to consulting standards.' },
  { Icon: DescriptionRounded, title: 'Frameworks and market entry', body: 'Porter, BCG, 7S, value chain, market-entry, each laid out correctly on the firm master and palette, the market Mekko native.' },
  { Icon: AutorenewRounded, title: 'Recurring client reporting', body: 'Weekly and monthly status decks, refreshed from new data on the client’s template every cycle.' },
  { Icon: BarChartRounded, title: 'Chart-heavy rebuilds', body: 'New model run, same 30 chart pages, updated in place instead of rebuilt by hand.' },
  { Icon: TravelExploreRounded, title: 'Research to client-ready slides', body: 'Restack turns your existing research into client-ready SteerCo slides on the firm master.' },
  { Icon: CampaignRounded, title: 'Proposals and pitch decks', body: 'A scoping note and prior wins turned into a branded pitch on the firm template.' },
  { Icon: SwapHorizRounded, title: 'Template and brand migration', body: 'Legacy decks moved onto a current template, or the brand enforced across a library in one pass.' },
  { Icon: SchoolRounded, title: 'Workshop and training materials', body: 'Session decks and training materials built from your outline, on the firm template.' },
];

// Shared FAQ order for the audience pages. The entries themselves live in
// FAQ_REPOSITORY (shared/services/faqChatService.js) and are pulled by id, so
// the page, the support widget, and the FAQPage schema can never drift apart.
// Ordered by how early the objection lands, and it always ends on `refund`,
// which carries the 30-day risk reversal.
const BASE_FAQ_IDS = [
  'how-works',
  'different-from-chatgpt',
  'research-quality',
  'frameworks',
  'security',
  'powerpoint-versions',
  'collaboration',
  'pricing',
  'refund',
];

// Per-audience metadata. `hero`/`fold` titles are JSX so the rust <em> accent
// renders through SectionTitle. `hero` heads the page, `fold` heads the cases
// column. `visual` keys into DECK_VISUALS for the hero proof panel, `shift`
// carries the problem-to-solution pair, `outcomes` the benefit set, and
// `faqIds` the page FAQ order.
export const AUDIENCES = {
  'private-equity': {
    slug: 'private-equity',
    navLabel: 'Private Equity',
    visual: 'deckQ',
    meta: {
      title: 'Restack for Private Equity | AI for Deal Teams',
      description: "One AI agent for deal teams: the LBO model in Excel, the IC memo and committee deck in PowerPoint, the diligence write-up in Word, all on the fund's templates.",
    },
    hero: {
      eyebrow: 'Solutions for Funds',
      title: <>The board pack and IC deck, built for <em>private equity</em></>,
      subtitle: "Restack runs the deliverables a PE team rebuilds by hand: the quarterly board pack, the IC deck, the value-creation plan, built from your model on the fund's template, every figure traced to source so it holds when the committee grills it. The deal team keeps the thesis; Restack builds the deck.",
    },
    fold: {
      eyebrow: 'For Funds',
      title: <>Private <em>Equity</em></>,
      subtitle: 'Where buyout teams put Restack to work, from diligence through the hold period and into LP reporting.',
    },
    shift: {
      before: "The numbers land and the same pack gets rebuilt by hand: last quarter's file reformatted, charts relinked, the value-creation bridge redrawn, the source hunt repeated when the committee asks where a figure came from.",
      after: "Point Restack at last quarter's deck and the new numbers. It rebuilds on the fund's template with the KPI pages and the bridge in place, and every figure traces to the cell, chart, or paragraph it came from.",
    },
    outcomes: [
      {
        title: 'The quarterly rebuild stops being a rebuild',
        body: 'New numbers, same board template. The pack comes back on format, portco by portco, instead of being reassembled from last quarter every cycle.',
      },
      {
        title: 'Every figure holds up in committee',
        body: 'Each number traces to its source: a cell, a chart, a paragraph. When the IC asks where it came from, you show them in one click.',
      },
      {
        title: 'The deal team stays on the thesis',
        body: "Diligence synthesis, pipeline pages, and LP materials arrive drafted on the fund's template, so the team's hours go to judgment rather than formatting.",
      },
    ],
    faqIds: BASE_FAQ_IDS,
    cases: PE_CASES,
  },
  'growth-equity': {
    slug: 'growth-equity',
    navLabel: 'Growth Equity',
    visual: 'research',
    meta: {
      title: 'Restack for Growth Equity | AI for Growth Investors',
      description: "One AI agent for growth investors: the market model and cohorts in Excel, the thesis deck in PowerPoint, the sourcing memo in Word, on your firm's templates.",
    },
    hero: {
      eyebrow: 'Solutions for Funds',
      title: <>The market map and thesis deck, built for <em>growth equity</em></>,
      subtitle: "Restack runs the work that wins conviction: the market map, the bottom-up sizing, the thesis deck, the sourcing memo, the quarterly board pack, built on your firm's template with every figure cited, so the team covers more markets without adding headcount.",
    },
    fold: {
      eyebrow: 'For Funds',
      title: <>Growth <em>Equity</em></>,
      subtitle: 'Where growth investors build conviction, from the market map and thesis through to portfolio board decks.',
    },
    shift: {
      before: 'A new market starts from a blank page: filings, reports, and databases pulled by hand, the sizing rebuilt in a fresh file, the landscape redrawn on the firm template before anyone reads the thesis.',
      after: 'Restack does the pull, the bottom-up build, and the layout on your template, with the methodology written down and every figure cited, so the partner meeting argues about the thesis and not the arithmetic.',
    },
    outcomes: [
      {
        title: 'The team covers more markets without adding headcount',
        body: 'A market map, a sizing, and a screening memo are a request rather than a week, so the funnel is not capped by who is free to build the deck.',
      },
      {
        title: 'The sizing is defensible, not asserted',
        body: 'Bottom-up built and cross-checked top-down, with the methodology cited, so the number survives the first question about it.',
      },
      {
        title: 'Portfolio reporting refreshes instead of restarting',
        body: 'Attach the new metrics and the KPI and cohort pages update in place, on the same board template each quarter.',
      },
    ],
    faqIds: BASE_FAQ_IDS,
    cases: GE_CASES,
  },
  'consulting': {
    slug: 'consulting',
    navLabel: 'Consulting',
    visual: 'slideBuild',
    meta: {
      title: 'Restack for Consulting | AI for Case Teams',
      description: "One AI agent for case teams: the model in Excel, the client deck in PowerPoint, the report in Word, all on the firm's template with every figure cited.",
    },
    hero: {
      eyebrow: 'Solutions for Case Teams',
      title: <>The SteerCo deck and final report, built for <em>consulting teams</em></>,
      subtitle: 'Restack runs your client deliverables, built like a consultant: the SteerCo deck, the final report, the market sizing, the recurring status pack, with action titles, the Pyramid Principle, and your frameworks on the firm master, every figure sourced. You bring the judgment; Restack builds the deliverable.',
    },
    fold: {
      eyebrow: 'For Case Teams',
      title: <>Consulting <em>Teams</em></>,
      subtitle: 'Where case teams use Restack across engagements, from research output to recurring client decks.',
    },
    shift: {
      before: 'The analysis is done and the deck still takes the evening: action titles rewritten, the firm master reapplied page by page, thirty chart pages rebuilt because the model ran again.',
      after: 'Restack builds the deliverable on the firm master with action titles and the Pyramid Principle, and updates the chart pages in place when the model changes, so a late number does not cost the team a night.',
    },
    outcomes: [
      {
        title: 'The deliverable lands to consulting standards',
        body: 'Action titles, the Pyramid Principle, and the so-what, laid out on the firm master and palette rather than cleaned up onto it afterwards.',
      },
      {
        title: 'Chart pages update instead of getting rebuilt',
        body: 'A new model run refreshes the same chart pages in place, so the last-minute rerun costs minutes rather than the evening.',
      },
      {
        title: 'Recurring client reporting runs the same way every cycle',
        body: "Weekly and monthly status decks come back from new data on the client's template, in the format the client already reads.",
      },
    ],
    faqIds: BASE_FAQ_IDS,
    cases: CONSULTING_CASES,
  },
};

export const AUDIENCE_SLUGS = Object.keys(AUDIENCES);
